import { useEffect } from 'react';

/* Scroll-reveal for the Level 7 landing sections.
   ---------------------------------------------------------------------------
   Tags the section headers, cards and figures inside every `.l7` block and adds
   `.is-in` as each scrolls into view. One observer for the whole page rather than
   one per component.

   Deliberately opt-in per element (`data-reveal` is added here, not authored into
   the markup) so that if this never runs — script error, JS disabled — nothing is
   left hidden: the CSS only hides elements that carry the attribute. */

const TARGETS = '.l7 .l7-head, .l7 .l7-card, .l7 .l7-figure, .l7 .l7-split > div, .l7 ol > li';

export default function L7Reveal() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const els = Array.from(document.querySelectorAll<HTMLElement>(TARGETS));
    if (!els.length) return;

    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);       // one-shot: never animate back out
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );

    for (const el of els) {
      el.setAttribute('data-reveal', '');
      // Anything already on screen at mount gets shown immediately, so the top of
      // the page never sits blank waiting for a scroll that may not come.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) el.classList.add('is-in');
      else io.observe(el);
    }

    return () => io.disconnect();
  }, []);

  return null;
}
